/**
 * CMS SDK - ContentList 组件
 * 内容列表展示
 */

import React, { useCallback } from 'react';
import { t } from '../i18n';
import { ContentStatus } from '../types';

// 状态徽章颜色映射
const statusColors = {
  [ContentStatus.DRAFT]: '#6b7280',
  [ContentStatus.PENDING]: '#f59e0b',
  [ContentStatus.APPROVED]: '#10b981',
  [ContentStatus.REJECTED]: '#ef4444',
  [ContentStatus.ARCHIVED]: '#6b7280',
  [ContentStatus.SCHEDULED]: '#8b5cf6',
};

// 状态文本映射
const statusTextMap = {
  [ContentStatus.DRAFT]: 'statusDraft',
  [ContentStatus.PENDING]: 'statusPending',
  [ContentStatus.APPROVED]: 'statusApproved',
  [ContentStatus.REJECTED]: 'statusRejected',
  [ContentStatus.ARCHIVED]: 'statusArchived',
  [ContentStatus.SCHEDULED]: 'statusScheduled',
};

/**
 * 内容列表组件
 * @param {Object} props - 组件属性
 */
export function ContentList({
  contents = [],
  loading = false,
  selectedIds = [],
  onSelect,
  onSelectAll,
  onEdit,
  onDelete,
  onPublish,
  onView,
  className = '',
}) {
  // 格式化日期
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // 格式化数字
  const formatNumber = useCallback((num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + 'w';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num?.toString() || '0';
  }, []);

  // 选择单个
  const handleSelect = useCallback((id) => {
    if (!onSelect) return;
    if (selectedIds.includes(id)) {
      onSelect(selectedIds.filter(i => i !== id));
    } else {
      onSelect([...selectedIds, id]);
    }
  }, [selectedIds, onSelect]);

  // 全选
  const handleSelectAll = useCallback(() => {
    if (!onSelectAll) return;
    if (selectedIds.length === contents.length) {
      onSelectAll([]);
    } else {
      onSelectAll(contents.map(c => c.id));
    }
  }, [selectedIds, contents, onSelectAll]);

  if (loading && contents.length === 0) {
    return (
      <div className={`cms-content-list cms-content-list-loading ${className}`}>
        <div className="cms-loading-spinner" />
        <span>{t('loading')}</span>
      </div>
    );
  }

  if (contents.length === 0) {
    return (
      <div className={`cms-content-list cms-content-list-empty ${className}`}>
        <div className="cms-empty-icon">📄</div>
        <span>{t('noContent')}</span>
      </div>
    );
  }

  return (
    <div className={`cms-content-list ${className}`}>
      <table className="cms-table">
        <thead>
          <tr>
            <th className="cms-th-checkbox">
              <input
                type="checkbox"
                checked={selectedIds.length === contents.length && contents.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            <th>{t('contentTitle')}</th>
            <th>{t('contentCategory')}</th>
            <th className="cms-th-status">状态</th>
            <th className="cms-th-stats">{t('contentViews')}</th>
            <th className="cms-th-stats">{t('contentLikes')}</th>
            <th className="cms-th-date">{t('contentUpdatedAt')}</th>
            <th className="cms-th-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          {contents.map(content => (
            <tr 
              key={content.id}
              className={selectedIds.includes(content.id) ? 'cms-row-selected' : ''}
            >
              <td className="cms-td-checkbox">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(content.id)}
                  onChange={() => handleSelect(content.id)}
                />
              </td>
              <td className="cms-td-title">
                <div className="cms-content-info">
                  {content.cover_url && (
                    <img 
                      src={content.cover_url} 
                      alt="" 
                      className="cms-content-cover"
                    />
                  )}
                  <div className="cms-content-text">
                    <span className="cms-content-title">{content.title}</span>
                    {content.summary && (
                      <span className="cms-content-summary">{content.summary}</span>
                    )}
                  </div>
                </div>
              </td>
              <td className="cms-td-category">
                {content.category?.name || '-'}
              </td>
              <td className="cms-td-status">
                <span 
                  className="cms-status-badge"
                  style={{ backgroundColor: statusColors[content.status] }}
                >
                  {t(statusTextMap[content.status]) || content.status}
                </span>
              </td>
              <td className="cms-td-stats">
                {formatNumber(content.views)}
              </td>
              <td className="cms-td-stats">
                {formatNumber(content.likes)}
              </td>
              <td className="cms-td-date">
                {formatDate(content.updated_at)}
              </td>
              <td className="cms-td-actions">
                <div className="cms-action-buttons">
                  {onView && (
                    <button 
                      className="cms-action-btn"
                      onClick={() => onView(content)}
                      title={t('preview')}
                    >
                      👁
                    </button>
                  )}
                  {onEdit && (
                    <button 
                      className="cms-action-btn"
                      onClick={() => onEdit(content)}
                      title={t('edit')}
                    >
                      ✏️
                    </button>
                  )}
                  {onPublish && content.status === ContentStatus.DRAFT && (
                    <button 
                      className="cms-action-btn cms-action-publish"
                      onClick={() => onPublish(content)}
                      title={t('publish')}
                    >
                      🚀
                    </button>
                  )}
                  {onDelete && (
                    <button 
                      className="cms-action-btn cms-action-delete"
                      onClick={() => onDelete(content)}
                      title={t('delete')}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ContentList;
